/**
 * VideoTrimmer - Video player with timeline range selection
 *
 * Features:
 * - Video playback with play/pause controls
 * - Timeline scrubber for video navigation
 * - Range selection with draggable start/end handles
 * - Fixed duration window that can be moved along timeline
 * - Visual feedback for selected range
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { formatDuration } from '../utils/videoUtils';

/**
 * VideoTrimmer Component
 */
export default function VideoTrimmer({
  videoUrl,
  duration,
  maxBlurDuration,
  onTrimChange,
  initialStart = 0,
  initialEnd = null,
}) {
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isDragging, setIsDragging] = useState(null); // 'start', 'end', 'range', or null
  const [trimStart, setTrimStart] = useState(initialStart);
  const [trimEnd, setTrimEnd] = useState(initialEnd || Math.min(maxBlurDuration, duration));

  // Ensure trim range doesn't exceed max blur duration
  const trimDuration = trimEnd - trimStart;

  // Track if we just finished dragging to prevent timeupdate override
  const justFinishedDragging = useRef(false);

  // Track current trimStart in a ref to avoid stale closure in handleDragEnd
  const trimStartRef = useRef(trimStart);
  useEffect(() => {
    trimStartRef.current = trimStart;
  }, [trimStart]);

  // Handle video time update - only update when playing, not during drag
  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current && isPlaying && !isDragging && !justFinishedDragging.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, [isPlaying, isDragging]);

  // Play/Pause toggle
  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  // Seek to time
  const seekTo = useCallback((time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(time, duration));
      setCurrentTime(videoRef.current.currentTime);
    }
  }, [duration]);

  // Handle timeline click
  const handleTimelineClick = useCallback((e) => {
    if (!timelineRef.current || isDragging) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const time = percentage * duration;
    seekTo(time);
  }, [duration, seekTo, isDragging]);

  // Convert time to percentage position
  const timeToPercent = useCallback((time) => {
    return (time / duration) * 100;
  }, [duration]);

  // Generate time markers for the timeline
  const generateTimeMarkers = useCallback(() => {
    const markers = [];
    // Determine interval based on duration
    let interval;
    if (duration <= 10) {
      interval = 1; // Every 1 second for short videos
    } else if (duration <= 30) {
      interval = 2; // Every 2 seconds
    } else if (duration <= 60) {
      interval = 5; // Every 5 seconds
    } else if (duration <= 300) {
      interval = 15; // Every 15 seconds
    } else {
      interval = 30; // Every 30 seconds for long videos
    }

    // Add major markers at interval
    for (let t = 0; t <= duration; t += interval) {
      markers.push({ time: t, major: true });
    }

    // Add sub-second/minor markers (halfway between major markers)
    if (interval >= 2) {
      for (let t = interval / 2; t < duration; t += interval) {
        markers.push({ time: t, major: false });
      }
    }

    return markers.sort((a, b) => a.time - b.time);
  }, [duration]);

  // Handle drag start
  const handleDragStart = useCallback((type, e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(type);
  }, []);

  // Handle mouse/touch move during drag
  const handleDragMove = useCallback((e) => {
    if (!isDragging || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const time = percentage * duration;

    if (isDragging === 'start') {
      // Move start handle, keeping duration fixed
      const newStart = Math.max(0, Math.min(time, duration - maxBlurDuration));
      const newEnd = Math.min(duration, newStart + trimDuration);
      setTrimStart(newStart);
      setTrimEnd(newEnd);
    } else if (isDragging === 'end') {
      // Move end handle, keeping duration fixed
      const newEnd = Math.max(maxBlurDuration, Math.min(time, duration));
      const newStart = Math.max(0, newEnd - trimDuration);
      setTrimStart(newStart);
      setTrimEnd(newEnd);
    } else if (isDragging === 'range') {
      // Move entire range
      const rangeCenter = time;
      const halfDuration = trimDuration / 2;
      let newStart = rangeCenter - halfDuration;
      let newEnd = rangeCenter + halfDuration;

      // Clamp to bounds
      if (newStart < 0) {
        newStart = 0;
        newEnd = trimDuration;
      }
      if (newEnd > duration) {
        newEnd = duration;
        newStart = duration - trimDuration;
      }

      setTrimStart(Math.max(0, newStart));
      setTrimEnd(Math.min(duration, newEnd));
    }
  }, [isDragging, duration, maxBlurDuration, trimDuration]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      // Set flag to prevent timeupdate from overriding position
      justFinishedDragging.current = true;

      // Use ref to get current trimStart (avoids stale closure)
      const currentTrimStart = trimStartRef.current;

      setIsDragging(null);

      // Force seek to trimStart AFTER state update using multiple mechanisms
      if (videoRef.current) {
        videoRef.current.currentTime = currentTrimStart;
        setCurrentTime(currentTrimStart);

        // Also force seek after a small delay to override any video events
        requestAnimationFrame(() => {
          if (videoRef.current) {
            videoRef.current.currentTime = currentTrimStart;
            setCurrentTime(currentTrimStart);
          }
        });

        // And again with setTimeout as backup
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.currentTime = currentTrimStart;
            setCurrentTime(currentTrimStart);
          }
          justFinishedDragging.current = false;
        }, 100);
      }

      // Notify parent of trim change
      onTrimChange?.(trimStart, trimEnd);
    }
  }, [isDragging, trimStart, trimEnd, onTrimChange]);

  // Seek to start of selection when trimStart changes (during drag only)
  useEffect(() => {
    if (videoRef.current && isDragging) {
      // Only update playhead position during drag, not when pausing
      videoRef.current.currentTime = trimStart;
      setCurrentTime(trimStart);
    }
  }, [trimStart, isDragging]);

  // Add global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => handleDragMove(e);
      const handleEnd = () => handleDragEnd();

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);

      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Notify parent on initial mount
  useEffect(() => {
    onTrimChange?.(trimStart, trimEnd);
  }, []);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', () => setIsPlaying(false));

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', () => setIsPlaying(false));
    };
  }, [handleTimeUpdate]);

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
      {/* Video Player */}
      <div className="relative bg-black aspect-video">
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          onClick={togglePlay}
        />

        {/* Play/Pause Overlay */}
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity"
        >
          <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
            {isPlaying ? (
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </div>
        </button>

        {/* Current Time Badge */}
        <div className="absolute bottom-3 right-3 bg-black/70 px-2 py-1 rounded text-sm text-white">
          {formatDuration(currentTime)} / {formatDuration(duration)}
        </div>
      </div>

      {/* Timeline Controls */}
      <div className="p-4">
        {/* Info Bar */}
        <div className="flex items-center justify-between mb-3 text-sm">
          <div className="text-gray-400">
            Select <span className="text-emerald-400 font-medium">{formatDuration(maxBlurDuration)}</span> to blur
          </div>
          <div className="text-white font-medium">
            {formatDuration(trimStart)} - {formatDuration(trimEnd)}
            <span className="text-gray-400 ml-2">({formatDuration(trimDuration)})</span>
          </div>
        </div>

        {/* Timeline */}
        <div
          ref={timelineRef}
          className="relative h-12 bg-gray-700 rounded-lg cursor-pointer select-none"
          onClick={handleTimelineClick}
        >
          {/* Time markers (tick marks) */}
          {generateTimeMarkers().map((marker, idx) => (
            <div
              key={idx}
              className={`absolute bottom-0 ${marker.major ? 'h-3 w-px bg-gray-500' : 'h-1.5 w-px bg-gray-600'}`}
              style={{ left: `${timeToPercent(marker.time)}%` }}
            >
              {marker.major && marker.time > 0 && marker.time < duration && (
                <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[10px] text-gray-500 whitespace-nowrap">
                  {formatDuration(marker.time)}
                </span>
              )}
            </div>
          ))}

          {/* Non-selected regions (dimmed) */}
          <div
            className="absolute top-0 left-0 h-full bg-gray-900/60 rounded-l-lg"
            style={{ width: `${timeToPercent(trimStart)}%` }}
          />
          <div
            className="absolute top-0 right-0 h-full bg-gray-900/60 rounded-r-lg"
            style={{ width: `${100 - timeToPercent(trimEnd)}%` }}
          />

          {/* Selected range */}
          <div
            className={`absolute top-0 h-full bg-emerald-500/30 border-2 border-emerald-500 cursor-move ${
              isDragging === 'range' ? 'border-emerald-400' : ''
            }`}
            style={{
              left: `${timeToPercent(trimStart)}%`,
              width: `${timeToPercent(trimEnd) - timeToPercent(trimStart)}%`
            }}
            onMouseDown={(e) => handleDragStart('range', e)}
            onTouchStart={(e) => handleDragStart('range', e)}
          >
            {/* Drag instruction */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="flex items-center gap-1 text-emerald-300 text-xs font-medium bg-emerald-900/50 px-2 py-1 rounded">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
                Drag to move
              </div>
            </div>
          </div>

          {/* Start Handle */}
          <div
            className={`absolute top-0 h-full w-3 cursor-ew-resize z-10 ${
              isDragging === 'start' ? 'bg-emerald-400' : 'bg-emerald-500 hover:bg-emerald-400'
            }`}
            style={{ left: `calc(${timeToPercent(trimStart)}% - 6px)` }}
            onMouseDown={(e) => handleDragStart('start', e)}
            onTouchStart={(e) => handleDragStart('start', e)}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-full" />
          </div>

          {/* End Handle */}
          <div
            className={`absolute top-0 h-full w-3 cursor-ew-resize z-10 ${
              isDragging === 'end' ? 'bg-emerald-400' : 'bg-emerald-500 hover:bg-emerald-400'
            }`}
            style={{ left: `calc(${timeToPercent(trimEnd)}% - 6px)` }}
            onMouseDown={(e) => handleDragStart('end', e)}
            onTouchStart={(e) => handleDragStart('end', e)}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-full" />
          </div>

          {/* Current Time Indicator */}
          <div
            className="absolute top-0 h-full w-0.5 bg-white z-20 pointer-events-none"
            style={{ left: `${timeToPercent(currentTime)}%` }}
          >
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rounded-full" />
          </div>

          {/* Time markers */}
          <div className="absolute -bottom-5 left-0 text-xs text-gray-500">0:00</div>
          <div className="absolute -bottom-5 right-0 text-xs text-gray-500">{formatDuration(duration)}</div>
        </div>

        {/* Playback Controls */}
        <div className="flex items-center justify-center gap-4 mt-6">
          {/* Jump to start of trim */}
          <button
            onClick={() => seekTo(trimStart)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            title="Jump to start of selection"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 6h2v12H6V6zm3.5 6l8.5 6V6l-8.5 6z" />
            </svg>
          </button>

          {/* Play/Pause */}
          <button
            onClick={togglePlay}
            className="w-12 h-12 bg-emerald-500 hover:bg-emerald-600 rounded-full flex items-center justify-center text-white transition-colors"
          >
            {isPlaying ? (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-6 h-6 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {/* Jump to end of trim */}
          <button
            onClick={() => seekTo(trimEnd)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            title="Jump to end of selection"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 18l8.5-6L6 6v12zm2 0V6l6.5 6L8 18zm8-12h2v12h-2V6z" />
            </svg>
          </button>
        </div>

        {/* Instructions */}
        <p className="text-center text-gray-500 text-xs mt-4">
          Drag the green selection to choose which portion of the video to blur.
          Max duration: {formatDuration(maxBlurDuration)}
        </p>
      </div>
    </div>
  );
}
